import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Printer } from 'lucide-react';
import { api } from '@/lib/api';
import { documentoPapel } from '@/lib/imprimirPapel';
import { Botao } from '@/componentes/ui/botao';
import { Esqueleto } from '@/componentes/ui/primitivos';

/**
 * Um papel A4 aberto no EDGE, com prévia e escolha de impressora (ver
 * `abrirPapelNoEdge`): conferência cega, kardex, roteiro, folha de inventário.
 *
 * Fica fora do login e do AppShell, como o `/romaneio-a4`: o Edge não tem a
 * sessão, e quem autoriza é o token da URL.
 *
 * O PAPEL VAI NUM IFRAME SEM SCRIPT
 * ---------------------------------
 * O HTML veio de uma tela e é mostrado aqui, numa rota sem login. Num `div`
 * comum, um `<img onerror>` gravado por alguém rodaria com a origem do WMS na
 * máquina de quem abrisse o link. O `sandbox` sem `allow-scripts` impede isso;
 * `allow-same-origin` deixa esta página medir o papel e chamar o `print()`, e
 * `allow-modals` é o que o Chromium exige para o diálogo de impressão abrir.
 */
interface Papel {
  titulo: string;
  modo: 'papel' | 'pagina';
  css: string | null;
  html: string;
}

/**
 * O documento do modo `pagina`: as folhas de estilo do próprio WMS (as mesmas
 * que esta página carregou) e a `.folha` copiada. O `@media print` do
 * aplicativo faz o resto — A4 deitado, como o Ctrl+P da tela fazia.
 */
function documentoPagina(titulo: string, html: string): string {
  const estilos = Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style'))
    .map((e) => e.outerHTML)
    .join('\n');
  const tituloSeguro = titulo.replace(/&/g, '&amp;').replace(/</g, '&lt;');
  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<base href="${window.location.origin}/">
<title>${tituloSeguro}</title>
${estilos}
<style>
  body { background: #fff; padding: 8px; }
  /* Na prévia a folha mostra tudo: a rolagem interna da tela não existe aqui. */
  .folha, .folha * { max-height: none !important; overflow: visible !important; }
</style>
</head>
<body>${html}</body>
</html>`;
}

export function PapelA4(): JSX.Element {
  const { token = '' } = useParams();
  const quadro = React.useRef<HTMLIFrameElement>(null);
  const jaAbriu = React.useRef(false);
  const [carregado, definirCarregado] = React.useState(false);

  const consulta = useQuery<Papel>({
    queryKey: ['papel-a4', token],
    queryFn: () => api.get<Papel>(`/api/papel-a4/${encodeURIComponent(token)}`),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const documento = React.useMemo(() => {
    const p = consulta.data;
    if (!p) return '';
    return p.modo === 'papel' ? documentoPapel(p.titulo, p.css ?? '', p.html) : documentoPagina(p.titulo, p.html);
  }, [consulta.data]);

  React.useEffect(() => {
    if (consulta.data) document.title = consulta.data.titulo;
  }, [consulta.data]);

  const imprimir = React.useCallback((): void => {
    const janela = quadro.current?.contentWindow;
    if (!janela) return;
    janela.focus();
    janela.print();
  }, []);

  /*
   * Abre o diálogo sozinho uma vez — quem clicou em imprimir quer imprimir.
   * Espera o layout existir (o `load` do iframe vem antes das fontes e das
   * folhas de estilo terem medido tudo; sem layout, o papel sai branco).
   */
  React.useEffect(() => {
    if (!carregado || jaAbriu.current) return;
    jaAbriu.current = true;
    const doc = quadro.current?.contentDocument;
    const inicio = Date.now();
    const tentar = (): void => {
      if ((doc?.body?.getBoundingClientRect().height ?? 0) > 10 || Date.now() - inicio > 3000) {
        window.setTimeout(imprimir, 150);
        return;
      }
      window.setTimeout(tentar, 50);
    };
    tentar();
  }, [carregado, imprimir]);

  const deitado = consulta.data?.modo === 'pagina';

  return (
    <div className="min-h-screen bg-superficie2 p-4">
      <div className={deitado ? 'mx-auto max-w-[310mm] space-y-3' : 'mx-auto max-w-[230mm] space-y-3'}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-[18px] font-bold leading-tight">{consulta.data?.titulo ?? 'Papel A4'}</h1>
          <Botao variante="primaria" onClick={imprimir} disabled={!carregado}>
            <Printer className="h-4 w-4" />
            Imprimir
          </Botao>
        </div>

        {consulta.isLoading ? (
          <Esqueleto className="h-[297mm] w-full" />
        ) : consulta.error ? (
          <p className="flex items-start gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[13px] text-status-divergencia">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {consulta.error.message}
          </p>
        ) : documento ? (
          <iframe
            ref={quadro}
            title={consulta.data?.titulo ?? 'Papel A4'}
            sandbox="allow-same-origin allow-modals"
            srcDoc={documento}
            onLoad={() => definirCarregado(true)}
            className="cartao h-[calc(100vh-90px)] w-full bg-white"
          />
        ) : null}
      </div>
    </div>
  );
}
